import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Upload, Loader2, FileText, CheckCircle2, X,
  AlertCircle, User, Mail, Phone, Shield, Send
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { supabase } from '@/utils/supabase';

interface LoanOfficerInfo {
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  phone?: string;
  nmls?: string;
  title?: string;
  photoUrl?: string;
}

interface CompanyInfo {
  name: string;
  email?: string;
  logoUrl?: string;
  primaryColor?: string;
}

interface TokenData {
  tokenId: string;
  leadId: string;
  borrower: { firstName: string; lastName: string };
  loanOfficer: LoanOfficerInfo;
  company: CompanyInfo;
  existingDocuments: Array<{
    id: string;
    file_name: string;
    file_size: number;
    uploaded_at: string;
    status: string;
  }>;
  expiresAt: string;
}

interface UploadedFile {
  id: string;
  fileName: string;
  fileSize: number;
  uploadedAt: string;
  status: 'uploading' | 'success' | 'error';
  error?: string;
}

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) {
return `${bytes} B`;
}
  if (bytes < 1024 * 1024) {
return `${(bytes / 1024).toFixed(1)} KB`;
}
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const LeadDocumentUploadPortal = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tokenData, setTokenData] = useState<TokenData | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!token) {
      setError('Invalid link');
      setLoading(false);
      return;
    }

    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      const response = await fetch(
        `${supabase.supabaseUrl}/functions/v1/validate-lead-upload-token`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token })
        }
      );

      const result = await response.json();

      if (!response.ok) {
        setError(result.error || 'Invalid link');
        return;
      }

      setTokenData(result);

      // Convert existing docs to our format
      if (result.existingDocuments?.length > 0) {
        setUploadedFiles(result.existingDocuments.map((doc: any) => ({
          id: doc.id,
          fileName: doc.file_name,
          fileSize: doc.file_size,
          uploadedAt: doc.uploaded_at,
          status: 'success' as const
        })));
      }
    } catch (err) {
      setError('Failed to validate link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const uploadFile = async (file: File) => {
    if (!token) {
return;
}

    const fileId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;

    const newFile: UploadedFile = {
      id: fileId,
      fileName: file.name,
      fileSize: file.size,
      uploadedAt: new Date().toISOString(),
      status: 'uploading'
    };

    setUploadedFiles(prev => [...prev, newFile]);

    try {
      const formData = new FormData();
      formData.append('token', token);
      formData.append('file', file);

      const response = await fetch(
        `${supabase.supabaseUrl}/functions/v1/upload-lead-document`,
        {
          method: 'POST',
          body: formData
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Upload failed');
      }

      const result = await response.json();

      setUploadedFiles(prev =>
        prev.map(f =>
          f.id === fileId
            ? { ...f, id: result.documentId || fileId, status: 'success' as const }
            : f
        )
      );

      toast.success(`${file.name} uploaded successfully`);

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Upload failed';

      setUploadedFiles(prev =>
        prev.map(f =>
          f.id === fileId
            ? { ...f, status: 'error' as const, error: errorMessage }
            : f
        )
      );

      toast.error(errorMessage);
    }
  };

  const validateFile = (file: File): boolean => {
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error(`${file.name}: Only PDF, JPG, and PNG files are allowed`);
      return false;
    }

    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(`${file.name}: File must be less than 10MB`);
      return false;
    }

    return true;
  };

  const handleFiles = (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    fileArray.forEach(file => {
      if (validateFile(file)) {
        uploadFile(file);
      }
    });
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
    e.target.value = '';
  };

  const removeFile = (fileId: string) => {
    setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const handleComplete = () => {
    setIsComplete(true);
    toast.success('Thank you! Your documents have been submitted.', {
      description: `${tokenData?.loanOfficer.fullName} will review them shortly.`
    });
  };

  // Loading state
  if (loading) {
    return (
      <div className="h-screen overflow-y-auto bg-gradient-to-br from-gray-50 via-blue-50/30 to-cyan-50/20 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-cyan-600 mx-auto mb-4" />
          <p className="text-gray-600">Validating your link...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="h-screen overflow-y-auto bg-gradient-to-br from-gray-50 via-blue-50/30 to-cyan-50/20 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="h-8 w-8 text-red-600" />
            </div>
            <CardTitle className="text-xl text-gray-900">Link Not Valid</CardTitle>
            <CardDescription className="text-gray-600 mt-2">{error}</CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-sm text-gray-500">
              Please contact your loan officer for a new upload link.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success/complete state
  if (isComplete) {
    return (
      <div className="h-screen overflow-y-auto bg-gradient-to-br from-gray-50 via-blue-50/30 to-cyan-50/20 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="h-8 w-8 text-green-600" />
            </div>
            <CardTitle className="text-xl text-gray-900">Documents Submitted!</CardTitle>
            <CardDescription className="text-gray-600 mt-2">
              {tokenData?.loanOfficer.fullName} has been notified and will review your documents.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <p className="text-sm text-gray-600 mb-2">Need to upload more?</p>
              <Button variant="outline" onClick={() => setIsComplete(false)}>
                Upload More Documents
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!tokenData) {
return null;
}

  const { loanOfficer, company, borrower } = tokenData;
  const successfulUploads = uploadedFiles.filter(f => f.status === 'success').length;
  const isUploading = uploadedFiles.some(f => f.status === 'uploading');

  return (
    <div className="h-screen overflow-y-auto bg-gradient-to-br from-gray-50 via-blue-50/30 to-cyan-50/20 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 py-4 px-6">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          {company.logoUrl ? (
            <img src={company.logoUrl} alt={company.name} className="h-10 object-contain" />
          ) : (
            <span className="text-xl font-bold text-gray-900">{company.name}</span>
          )}
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Shield className="h-4 w-4" />
            <span>Secure Upload</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 p-6 md:p-8">
        <div className="max-w-2xl mx-auto">
          <Card className="shadow-xl border-0">
            <CardHeader className="border-b border-gray-100">
              {/* Loan Officer Card */}
              <div className="flex items-start gap-4">
                <Avatar className="h-16 w-16 border-2 border-cyan-200">
                  <AvatarImage src={loanOfficer.photoUrl} />
                  <AvatarFallback className="bg-gradient-to-br from-cyan-500 to-blue-600 text-white text-lg">
                    {loanOfficer.firstName[0]}{loanOfficer.lastName[0]}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <CardTitle className="text-xl text-gray-900">
                    Upload documents for {loanOfficer.fullName}
                  </CardTitle>
                  {loanOfficer.title && (
                    <p className="text-sm text-gray-500 mt-1">{loanOfficer.title}</p>
                  )}
                  <div className="flex flex-wrap gap-4 mt-3 text-sm">
                    <a
                      href={`mailto:${loanOfficer.email}`}
                      className="flex items-center gap-1.5 text-cyan-600 hover:text-cyan-700"
                    >
                      <Mail className="h-4 w-4" />
                      {loanOfficer.email}
                    </a>
                    {loanOfficer.phone && (
                      <a
                        href={`tel:${loanOfficer.phone}`}
                        className="flex items-center gap-1.5 text-cyan-600 hover:text-cyan-700"
                      >
                        <Phone className="h-4 w-4" />
                        {loanOfficer.phone}
                      </a>
                    )}
                  </div>
                  {loanOfficer.nmls && (
                    <p className="text-xs text-gray-400 mt-2">NMLS# {loanOfficer.nmls}</p>
                  )}
                </div>
              </div>

              {borrower.firstName && (
                <CardDescription className="mt-4 text-base">
                  Hi {borrower.firstName}, please upload any documents related to your loan application below.
                </CardDescription>
              )}
            </CardHeader>

            <CardContent className="space-y-6 pt-6">
              {/* Dropzone */}
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                />

                <div
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className={cn(
                    "relative border-2 border-dashed rounded-xl p-8 text-center transition-all duration-300 cursor-pointer group",
                    isDragging && "border-cyan-500 bg-cyan-50 scale-[1.02]",
                    !isDragging && "border-gray-300 bg-gray-50/50 hover:border-cyan-400 hover:bg-cyan-50/50"
                  )}
                >
                  <div className="flex flex-col items-center gap-3">
                    <div className={cn(
                      "w-14 h-14 rounded-full flex items-center justify-center transition-all duration-300",
                      isDragging ? "bg-cyan-500 text-white" : "bg-gray-200 text-gray-500 group-hover:bg-cyan-500 group-hover:text-white"
                    )}>
                      <Upload className="h-6 w-6" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">
                        {isDragging ? 'Drop files here' : 'Drag & drop files or click to browse'}
                      </p>
                      <p className="text-sm text-gray-500 mt-1">
                        PDF, JPG, PNG up to 10MB each
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Uploaded Files List */}
              {uploadedFiles.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Uploaded Files ({successfulUploads})
                  </h3>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {uploadedFiles.map(file => (
                      <div
                        key={file.id}
                        className={cn(
                          "flex items-center gap-3 p-3 rounded-lg border transition-all",
                          file.status === 'success' && "bg-green-50 border-green-200",
                          file.status === 'uploading' && "bg-blue-50 border-blue-200",
                          file.status === 'error' && "bg-red-50 border-red-200"
                        )}
                      >
                        <div className={cn(
                          "flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center",
                          file.status === 'success' && "bg-green-500 text-white",
                          file.status === 'uploading' && "bg-blue-500 text-white",
                          file.status === 'error' && "bg-red-500 text-white"
                        )}>
                          {file.status === 'uploading' && <Loader2 className="h-4 w-4 animate-spin" />}
                          {file.status === 'success' && <CheckCircle2 className="h-4 w-4" />}
                          {file.status === 'error' && <AlertCircle className="h-4 w-4" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{file.fileName}</p>
                          <p className="text-xs text-gray-500">
                            {formatFileSize(file.fileSize)}
                            {file.error && <span className="text-red-600 ml-2">{file.error}</span>}
                          </p>
                        </div>
                        {file.status !== 'uploading' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              removeFile(file.id);
                            }}
                            className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-600 rounded"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>

            <CardFooter className="flex-col gap-4 border-t border-gray-100 pt-6">
              <Button
                onClick={handleComplete}
                disabled={successfulUploads === 0 || isUploading}
                className="w-full h-12 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700"
                size="lg"
              >
                {isUploading ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Send className="h-5 w-5 mr-2" />
                    Submit {successfulUploads > 0 ? `(${successfulUploads} ${successfulUploads === 1 ? 'file' : 'files'})` : 'Documents'}
                  </>
                )}
              </Button>

              <p className="text-xs text-center text-gray-500">
                Your documents are encrypted and securely transmitted.
              </p>
            </CardFooter>
          </Card>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-4 px-6">
        <div className="max-w-2xl mx-auto text-center text-sm text-gray-500">
          <p>&copy; {new Date().getFullYear()} {company.name}. All rights reserved.</p>
          {company.email && (
            <p className="mt-1">
              Need help? Contact <a href={`mailto:${company.email}`} className="text-cyan-600 hover:underline">{company.email}</a>
            </p>
          )}
        </div>
      </footer>
    </div>
  );
};
